import React, { useState, useMemo, useEffect } from 'react'
import KioskModeContext from '../context/KioskModeContext'
import { Route, Routes, Navigate } from 'react-router-dom'
import { KioskLayout } from '../routes/KioskLayout'
import { Kiosk } from '../features/kiosk/Kiosk'
import { EnrollWorkerNew } from '../features/user/EnrollWorkerNew'
import { GeoFence } from '../features/projects/geofence/GeoFence'
import { DialogComponent } from './Dialog'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { useAuth0 } from '@auth0/auth0-react'
import { InputLabel, FormControl, Select, MenuItem } from '@mui/material'
import { useGetProjectsAll } from '../features/projects/hooks'
import { SelectTimeSheet } from '../features/user/SelectTimesheet'
import { TimeCard } from '../features/timecard/TimeCard'
import { WorkerFacialRecognition } from '../features/user/face-auth/WorkerFacialRecogntion'

export const KioskMode = ({
  isKioskMode,
  children,
}: {
  isKioskMode: boolean
  children: any
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mode, setMode] = useState<boolean>(isKioskMode)
  const [showDialog, setShowDialog] = useState<boolean>(false)
  const [selectedProject, setSelectedProject] = useState<any>(null)
  const [showLocations, setShowLocations] = useState<boolean>(false)
  const [formIsValid, setFormIsValid] = useState<boolean>(false)
  const [formIsInValidMessage, setFormIsInValidMessage] = useState<string>('')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [kioskProject, setKioskProject] = useState<any>(null)
  const [exitingKiosk, setExitingKiosk] = useState<boolean>(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [appKioskMode, setAppKioskMode] = useLocalStorage('kioskMode', '')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [kioskProjectId, setKioskProjectId] = useLocalStorage(
    'kioskProject',
    ''
  )
  const [kioskProjectLocation, setKioskProjectLocation] = useLocalStorage(
    'kioskProjectLocation',
    ''
  )
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [kioskGeoFence, setKioskGeoFence] = useLocalStorage('kioskGeoFence', '')
  const {
     data: projects,
      isLoading,
       error,
       refetch 
      } = useGetProjectsAll('projects', showDialog)

  useEffect(() => {
    if (showDialog) {
      refetch()
    }
  }, [showDialog, refetch])
  
  const { logout } = useAuth0()
  const kioskMode = useMemo(
    () => ({
      toggleKioskMode: () => {
        setShowDialog(false)
        setMode(prevMode => (prevMode === true ? false : true))
        setAppKioskMode((prevMode: boolean) =>
          prevMode === true ? false : true
        )
      },
      setProjectFirst: () => {
        setShowDialog(prevMode => (prevMode === true ? false : true))
      },
      setByUrl: (
        accountId: String | undefined,
        projectId: String | undefined
      ) => {
        setKioskProject(projectId)
        setKioskProjectId(projectId)
        kioskMode.toggleKioskMode()
      },
    }),
    [setAppKioskMode, setKioskProject, setKioskProjectId]
  )

  const handleChange = (event: any) => {
    event.preventDefault()
    setFormIsValid(true)
    setFormIsInValidMessage('')
    setKioskProjectLocation(null)
    // event.target.value is the index of the project in projects array
    const currentProject: any = projects[event.target.value]

    if (!currentProject.kioskMode?.enabled) {
      setFormIsInValidMessage(
        `Kiosk mode is not enabled for this project. \nPlease ask your administrator to enable it.`
      )
      setFormIsValid(false)
    } else {
      if (currentProject.locations && currentProject.locations.length === 0)
        setShowLocations(false)
      if (currentProject.locations && currentProject.locations.length > 0) {
        setKioskProject(currentProject._id)
        setKioskProjectId(currentProject._id)
        setSelectedProject(currentProject)
      }

      if (currentProject.locations && currentProject.locations.length > 1) {
        setShowLocations(true)
      } else if (
        currentProject.locations &&
        currentProject.locations.length > 0
      ) {
        setShowLocations(false)
        setKioskProjectLocation(currentProject.locations[0])
        setKioskGeoFence([currentProject.locations[0]])
        setFormIsValid(true)
      }
    }
  }

  const handleLocationChange = (event: any) => {
    event.preventDefault()
    const currentLocation = selectedProject.locations[event.target.value]
    setKioskProjectLocation(currentLocation)
    setKioskGeoFence([currentLocation])
    setFormIsValid(true)
    setFormIsInValidMessage('')
  }

  const handleClose = () => {
    setShowDialog(false)
    setSelectedProject(null)
    setKioskProject(null)
    setShowLocations(false)
    setFormIsValid(false)
    setFormIsInValidMessage('')
  }

  const handleConfirmation = () => {
    if (showLocations && !kioskProjectLocation) {
      setFormIsValid(false)
      setFormIsInValidMessage('Please select a location.')
    } else {
      kioskMode.toggleKioskMode()
    }
  }
  return (
    <KioskModeContext.Provider value={kioskMode}>
      <form>
        <DialogComponent
          title='Turn on Kiosk Mode?'
          isOpen={showDialog}
          handleClose={() => handleClose()}
          handleConfirmation={handleConfirmation}
          isLoading={isLoading}
          acceptButtonText='Confirm'
          closeButtonText='Cancel'
          formIsValid={formIsValid}
          formIsInValidMessage={formIsInValidMessage}>
          Are you sure you want to turn on Kiosk Mode? This will prevent anyone
          from accessing the app. You will need to login to access the full
          admin dashboard.
          <br />
          <br />
          {projects && !error && showDialog ? (
            <>
              <FormControl sx={{ mt: 2, mb: 1, width: 300 }}>
                <InputLabel id='projectId'>Select Project</InputLabel>
                <Select
                  labelId='projectId'
                  id='projectId'
                  name='projectId'
                  variant='filled'
                  defaultValue=''
                  onChange={(e: any) => handleChange(e)}>
                  {projects &&
                    projects.map((project: any, index: number) => (
                      <MenuItem key={index} value={index}>
                        {project.projectName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              {!formIsValid && selectedProject?.locations?.length === 0 && (
                <p style={{ color: 'red' }}>
                  There are no locations for this project.
                </p>
              )}
            </>
          ) : null}
          {selectedProject && showLocations && !error && showDialog ? (
            <FormControl sx={{ mt: 2, mb: 1, width: 300 }}>
              <InputLabel id='projectId'>Select Location</InputLabel>
              <Select
                labelId='location'
                id='location'
                name='location'
                variant='filled'
                defaultValue=''
                onChange={(e: any) => handleLocationChange(e)}>
                {selectedProject.locations.map(
                  (location: any, index: number) => (
                    <MenuItem key={index} value={index}>
                      {location.address}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          ) : null}
        </DialogComponent>
      </form>
      {mode ? (
        <Routes>
          <Route element={<KioskLayout />}>
            <Route
              path='/'
              element={
                <Kiosk
                  onExit={() => {
                    setExitingKiosk(true)
                    localStorage.clear()
                    sessionStorage.clear()
                    logout({
                      logoutParams: {
                        returnTo: `${window.location.origin}/login`,
                      },
                    })
                    kioskMode.toggleKioskMode()
                  }}
                />
              }
            />
            <Route
              path='/projects/:id/geo-fence'
              element={<GeoFence selectedLocation={kioskProjectLocation} />}
            />
            <Route
              path='/projects/:id/select-worker'
              element={
                <WorkerFacialRecognition
                  selectedLocation={kioskProjectLocation}
                />
              }
            />
            <Route
              path='/projects/:id/select-timesheet'
              element={<SelectTimeSheet />}
            />
            <Route
              path='/projects/:id/timecard/:workerId/out/:timecardId'
              element={<TimeCard selectedLocation={kioskProjectLocation} />}
            />
            <Route
              path='/projects/:id/timecard/:workerId/in'
              element={<TimeCard selectedLocation={kioskProjectLocation} />}
            />
            <Route
              path='/:account/job-site/enrollment'
              element={<EnrollWorkerNew />}
            />
            <Route path='/*' element={<Navigate to='/' />} />
          </Route>
        </Routes>
      ) : !exitingKiosk ? (
        <>{children}</>
      ) : null}
    </KioskModeContext.Provider>
  )
}
